import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import { getReport, getAllSanityProfiles } from "~/lib/server-fns"
import { getMatchingProfiles, SECTION_LABELS, profiles as fallbackProfiles } from "~/data/profiles"
import { DualProfileCard } from "~/components/report/DualProfileCard"
import { captureHandledException } from "~/lib/handled-errors"

export const Route = createFileRoute("/report/select/$id")({
  component: DualProfileSelectorPage,
  loader: async ({ params }) => {
    async function loadSanityProfiles() {
      try {
        return await getAllSanityProfiles({ data: {} })
      } catch (err) {
        await captureHandledException(err, {
          feature: "report",
          action: "load-sanity-profiles",
          layer: "loader",
          fallback: "static-profiles",
          extra: { route: "report.select", reportId: params.id },
        })
        return null
      }
    }

    const [data, sanityProfiles] = await Promise.all([
      getReport({ data: params.id }),
      loadSanityProfiles(),
    ])

    if (data.selectedProfile) {
      throw redirect({
        to: "/report/$id",
        params: { id: params.id },
        search: { profile: undefined, force_dual: undefined },
      })
    }

    const allProfiles = sanityProfiles?.length ? sanityProfiles : fallbackProfiles

    const scores = {
      identity: data.identityScore,
      purpose: data.purposeScore,
      community: data.communityScore,
    }
    const result = getMatchingProfiles(scores, true, allProfiles)

    if (!result.isDualProfile) {
      throw redirect({ to: "/report/$id", params: { id: params.id }, search: { profile: undefined, force_dual: undefined } })
    }

    return { ...data, result }
  },
  errorComponent: () => (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl text-core-orange mb-4">Report Not Found</h1>
        <p className="text-white/60 mb-6">This report link may be invalid or expired.</p>
        <a
          href="/"
          className="inline-block bg-core-orange text-white font-bold py-2 px-6 rounded-full hover:opacity-90 transition-opacity"
        >
          Take the Assessment
        </a>
      </div>
    </div>
  ),
})

function DualProfileSelectorPage() {
  const data = Route.useLoaderData()
  const { primary, secondary } = data.result

  return (
    <div className="flex-1">
      <div className="max-w-[1080px] mx-auto">
        {/* Header */}
        <section className="flex flex-col items-center gap-4 px-4 sm:px-20 py-12">
          <h1 className="text-4xl text-core-orange tracking-widest text-center">
            {SECTION_LABELS.coreSnapshot}
          </h1>
          <p className="text-white/60 text-center">
            Your profile falls between two archetypes
          </p>
          <p className="text-white/80 text-center leading-relaxed max-w-[700px]">
            We believe you know yourself best. Read through both profiles below
            and choose the one that resonates most with where you are right now.
            There are no wrong answers {"\u2014"} this is about honest
            self-reflection, not a label.
          </p>
        </section>

        {/* Divider */}
        <div className="px-4 sm:px-[60px]">
          <div className="w-full h-px bg-white/10" />
        </div>

        {/* Prompt */}
        <section className="flex flex-col items-center gap-2 px-4 sm:px-[60px] py-6">
          <h2 className="text-2xl text-core-orange tracking-wider text-center">
            {SECTION_LABELS.whichProfile}
          </h2>
          <p className="text-white/60 text-center max-w-[600px] text-sm">
            Read both descriptions, then select the one that feels most true to
            where you are today.
          </p>
        </section>

        {/* Profile Cards */}
        <section className="flex flex-col sm:flex-row gap-6 px-4 sm:px-[60px] py-4">
          <DualProfileCard profile={primary} reportId={data.id} />
          {secondary && (
            <DualProfileCard profile={secondary} reportId={data.id} />
          )}
        </section>

        {/* Divider */}
        <div className="px-4 sm:px-[60px] py-4">
          <div className="w-full h-px bg-white/10" />
        </div>

        {/* Reassurance */}
        <section className="flex flex-col items-center gap-4 px-4 sm:px-20 py-8">
          <span className="text-core-orange text-3xl">{"\u2764"}</span>
          <h3 className="text-white font-heading text-xl text-center">
            {SECTION_LABELS.noWrongAnswers}
          </h3>
          <p className="text-white/60 text-center leading-relaxed max-w-[640px] text-[15px]">
            This isn{"\u2019"}t a test {"\u2014"} it{"\u2019"}s a mirror.
            Whichever profile you choose, you{"\u2019"}ll receive a personalized
            reflection with Scripture, encouragement, and practical next steps
            for your journey. You can always come back and explore the other
            profile later.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-white/40 text-sm">Not sure yet?</span>
            <Link
              to="/report/both/$id"
              params={{ id: data.id }}
              className="text-core-orange text-sm hover:underline"
            >
              Show me both profiles {"\u2192"}
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}
